import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { CheckoutPaymentInformationState, PaymentMethodPollingService } from '@platform-frontend/checkout';
import { ClearPaymentMethodVerification } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT, PollingConfiguration } from '@platform-frontend/platform';
import { PopupFeatures, PopupService } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-payment-method-with-polling',
  templateUrl: './payment-method-with-polling.component.html',
  styleUrls: ['./payment-method-with-polling.component.scss']
})
export class PaymentMethodWithPollingComponent implements OnInit, OnDestroy {
  constructor(
    private paymentMethodPollingService: PaymentMethodPollingService,
    private popupService: PopupService,
    private route: ActivatedRoute,
    private store: Store
  ) {}

  private readonly PAYMENT_METHOD_POPUP = 'payment-method-polling';

  pollingConfiguration: PollingConfiguration;

  ngOnInit(): void {
    this.initializePopup();
    this.pollingConfiguration = this.paymentMethodPollingService.getPollingConfiguration({
      showVerificationPage: url => this.redirectPopup(url),
      proceed: () => this.proceed(),
      cancel: () => this.cancel()
    });
  }

  private initializePopup(): void {
    const features = new PopupFeatures(460, 730);
    this.popupService.openOrNavigate(this.PAYMENT_METHOD_POPUP, PLATFORM_ENVIRONMENT.staticPagesUrl + '/en/paypal/loading', features);
    this.popupService
      .closedByUser(this.PAYMENT_METHOD_POPUP)
      .pipe(
        tap(() => this.cancel()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private redirectPopup(url: string): Observable<unknown> {
    this.popupService.openOrNavigate(this.PAYMENT_METHOD_POPUP, url, undefined, true);
    return of(null);
  }

  private proceed(): Observable<unknown> {
    const navExtras = { clearNotifications: false, relativeTo: this.route };
    return this.store.dispatch(new Navigate([this.getProceedUrl()], navExtras));
  }

  private getProceedUrl(): string {
    const paymentMethodCount = this.store.selectSnapshot(CheckoutPaymentInformationState.paymentMethods).length;
    if (paymentMethodCount <= 1) {
      return '../overview';
    } else {
      return '../payment-method-selection';
    }
  }

  cancel(): Observable<unknown> {
    const navExtras = { clearNotifications: false, relativeTo: this.route };
    return this.store.dispatch([new ClearPaymentMethodVerification(), new Navigate([this.getCancelUrl()], navExtras)]);
  }

  private getCancelUrl(): string {
    const paymentMethodCount = this.store.selectSnapshot(CheckoutPaymentInformationState.paymentMethods).length;
    if (paymentMethodCount === 0) {
      return '../overview';
    } else {
      return '../add-payment-method';
    }
  }

  ngOnDestroy(): void {
    this.popupService.close(this.PAYMENT_METHOD_POPUP);
  }
}
